import {
  ArrayParam,
  BooleanParam,
  NumberParam,
  StringParam,
} from "use-query-params";

import {
  KontaxPlan,
  NextVatDeclarationStatus,
  PaymentFrequency,
} from "../../../../types";
import { UserFilterOptions } from "../UserFilters";

/**
 * URL persistence for the VAT list (SMT-1354).
 *
 * The VAT list does not auto-load — unlike ZM, it only fetches once a filter
 * or "Load all" runs. Without the list's own state in the URL, a tab round
 * trip through ELSTER / Submissions / DFV (which read and write `?email=`
 * for the *selected* customer) would come back to VAT empty, because nothing
 * would tell the container to re-fetch. The sub navigation carries
 * `location.search`, so keeping the list's filters, page and "Load all" flag
 * in the URL lets the list restore itself on return.
 *
 * This is a **second, independent** param from the selection `email` (see
 * `useEmailParam` / `EMAIL_PARAM_NAME`): `email` says which customer the
 * sibling tabs (ELSTER, Submissions, DFV) work on; `filterEmail` says the
 * list is manually filtered to one address. They are equal whenever someone
 * types an email into the filter box, but only `email` is set by expanding a
 * row — see `seedVatListState` for the seeding rule this encodes.
 */
export const VAT_LIST_QUERY_CONFIG = {
  all: BooleanParam,
  filterEmail: StringParam,
  status: StringParam,
  frequency: StringParam,
  // ArrayParam (repeated keys, `plans=kontax&plans=kontax_sb`), not
  // DelimitedArrayParam: `kontax_sb` contains the default `_` delimiter, so a
  // delimited encoding would split it into two values.
  plans: ArrayParam,
  assigneeId: StringParam,
  page: NumberParam,
};

/** The decoded shape of `VAT_LIST_QUERY_CONFIG`, as `useQueryParams` returns it. */
export interface VatListQuery {
  all?: boolean | null;
  filterEmail?: string | null;
  status?: string | null;
  frequency?: string | null;
  plans?: (string | null)[] | null;
  assigneeId?: string | null;
  page?: number | null;
}

export const DEFAULT_VAT_PLANS = [KontaxPlan.KONTAX, KontaxPlan.KONTAX_SB];

export const DEFAULT_VAT_FILTER_OPTIONS: UserFilterOptions = {
  email: "",
  status: undefined,
  plans: DEFAULT_VAT_PLANS,
  vatPaymentFrequency: undefined,
};

const isNextVatDeclarationStatus = (
  value?: string | null
): value is NextVatDeclarationStatus =>
  Object.values(NextVatDeclarationStatus).includes(
    value as NextVatDeclarationStatus
  );

const isPaymentFrequency = (value?: string | null): value is PaymentFrequency =>
  Object.values(PaymentFrequency).includes(value as PaymentFrequency);

const isKontaxPlan = (value?: string | null): value is KontaxPlan =>
  Object.values(KontaxPlan).includes(value as KontaxPlan);

const samePlans = (a: KontaxPlan[], b: KontaxPlan[]): boolean =>
  a.length === b.length && [...a].sort().join(",") === [...b].sort().join(",");

interface SeedVatListStateOptions {
  emailParam?: string | null;
  withinTabs: boolean;
}

interface VatListState {
  filterOptions: UserFilterOptions;
  page: number;
  shouldFetch: boolean;
}

/**
 * Build the VAT list's initial filter/page state from the URL, and decide
 * whether it should fetch on mount.
 *
 * Email seeding rule (identical to ZM): a manually typed list filter
 * (`filterEmail`) always wins; otherwise, arriving from outside the VAT tabs
 * with a selected customer (`?email=`, e.g. from Alle Mandanten or the
 * customer drawer) filters the list to them, as today; but returning from a
 * sibling tab (`withinTabs`) must not re-narrow the list to the selection.
 *
 * The other URL values are hand-editable, so they are validated before they
 * reach `getUsersWithDeclarations`: `status`/`frequency`/`plans` fall back to
 * their defaults when they are not real enum values, and `page` falls back
 * to 1 when it is not a positive integer.
 */
export const seedVatListState = (
  query: VatListQuery,
  { emailParam, withinTabs }: SeedVatListStateOptions
): VatListState => {
  const email = query.filterEmail ?? (withinTabs ? "" : emailParam ?? "");

  const status = isNextVatDeclarationStatus(query.status)
    ? query.status
    : undefined;

  const vatPaymentFrequency = isPaymentFrequency(query.frequency)
    ? query.frequency
    : undefined;

  const validPlans = (query.plans ?? []).filter(isKontaxPlan);
  const plans = validPlans.length ? validPlans : DEFAULT_VAT_PLANS;

  const assigneeId = query.assigneeId ? query.assigneeId : undefined;

  const page =
    typeof query.page === "number" &&
    Number.isInteger(query.page) &&
    query.page > 0
      ? query.page
      : 1;

  const hasListParam = Boolean(
    query.all ||
      query.filterEmail ||
      query.status ||
      query.frequency ||
      query.assigneeId ||
      (query.page !== undefined && query.page !== null) ||
      query.plans?.length
  );

  return {
    filterOptions: { email, status, vatPaymentFrequency, plans, assigneeId },
    page,
    shouldFetch: hasListParam || Boolean(email),
  };
};

/**
 * Map the VAT list's current filters/page to query params for `setQuery`.
 * Values equal to their default are `undefined` so they leave the URL. Does
 * NOT include `all` — the container owns that flag directly (set by "Load
 * all", cleared by a manual email filter), since it is not derived from
 * `UserFilterOptions`.
 */
export const toVatListQuery = (
  filterOptions: UserFilterOptions,
  page: number
): Omit<VatListQuery, "all"> => ({
  filterEmail: filterOptions.email || undefined,
  status: filterOptions.status ?? undefined,
  frequency: filterOptions.vatPaymentFrequency ?? undefined,
  plans:
    filterOptions.plans && !samePlans(filterOptions.plans, DEFAULT_VAT_PLANS)
      ? filterOptions.plans
      : undefined,
  assigneeId: filterOptions.assigneeId || undefined,
  page: page > 1 ? page : undefined,
});
